/** Page d'affectation des plannings : sélection du planning de la semaine par employé.
 *
 * Liste TOUS les employés actifs (tous métiers). Pour chaque ligne, le menu déroulant
 * ne propose QUE les plannings dont l'activity correspond au métier de l'employé,
 * car le backend renvoie 400 si on affecte un planning d'un autre métier.
 */

import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { addDays, format, getISOWeek, startOfISOWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarOff, ChevronLeft, ChevronRight, FileText, RotateCcw } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useEmployees } from '../hooks/useEmployees';
import { useSites } from '../hooks/useSites';
import {
  useSchedules,
  useWeeklyView,
  useSetWeeklyOverride,
  useSetWeeklyAbsentDays,
  useSetDriverDay,
  type AbsenceReason,
  type AbsentDay,
  type DayRound,
} from '../hooks/useSchedules';
import { rounds as roundsResource, type Round } from '../hooks/useArbreapains';
import { authApi } from '../services/api';
import { ACTIVITY_LABEL, type EmployeeActivity } from '../types';

const API_URL = import.meta.env.VITE_API_URL;

const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

/** Télécharge le planning hebdo PDF d'une équipe (ou d'un seul employé). */
async function downloadWeeklyPlanning(opts: {
  week_start: string;
  activity: EmployeeActivity;
  site_id?: number;
  employee_id?: number;
}) {
  const token = localStorage.getItem('token');
  const params = new URLSearchParams({ week_start: opts.week_start, activity: opts.activity });
  if (opts.site_id) params.set('site_id', String(opts.site_id));
  if (opts.employee_id) params.set('employee_id', String(opts.employee_id));
  const res = await fetch(`${API_URL}/api/reports/weekly-planning?${params}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    alert('Erreur lors de la génération du PDF');
    return;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `planning-${opts.employee_id ?? opts.activity}-${opts.week_start}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Palette de couleurs cyclique pour différencier visuellement les plannings */
const PALETTE = [
  { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300', bar: 'bg-indigo-500' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300', bar: 'bg-emerald-500' },
  { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', bar: 'bg-amber-500' },
  { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-300', bar: 'bg-rose-500' },
  { bg: 'bg-sky-100', text: 'text-sky-700', border: 'border-sky-300', bar: 'bg-sky-500' },
  { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-300', bar: 'bg-violet-500' },
  { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-300', bar: 'bg-teal-500' },
  { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', bar: 'bg-orange-500' },
] as const;

function colorFor(scheduleId: number | null | undefined) {
  if (!scheduleId) return null;
  return PALETTE[scheduleId % PALETTE.length];
}

export function AssignmentPage() {
  // Semaine courante (lundi) par défaut
  const [weekStart, setWeekStart] = useState<Date>(() => startOfISOWeek(new Date()));
  const [siteFilter, setSiteFilter] = useState<number | undefined>();
  const [teamFilter, setTeamFilter] = useState<EmployeeActivity | ''>('');

  const weekStartIso = format(weekStart, 'yyyy-MM-dd');
  const weekEnd = addDays(weekStart, 6);

  // Un chef d'équipe est verrouillé sur son équipe (le backend filtre déjà)
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => authApi.me(),
    staleTime: 60_000,
  });
  const isManager = me?.role === 'manager';
  const effectiveTeam: EmployeeActivity | '' = isManager ? me?.activity ?? '' : teamFilter;

  const { data: sites } = useSites();
  // Tous les plannings types (tous métiers) — on filtre par employé côté UI
  const { data: schedules } = useSchedules();
  // Tous les employés actifs pour connaître le métier de chacun
  const { data: employeesData } = useEmployees({ per_page: 100, active_only: true });
  const { data: weekly, isLoading } = useWeeklyView(weekStartIso, siteFilter);
  const { data: roundsList } = roundsResource.useList();
  const setOverride = useSetWeeklyOverride();
  const setAbsentDays = useSetWeeklyAbsentDays();
  const setDriverDay = useSetDriverDay();

  // Index métier par employé
  const activityByEmployee = useMemo(() => {
    const m = new Map<number, EmployeeActivity>();
    for (const e of employeesData?.items ?? []) m.set(e.id, e.activity);
    return m;
  }, [employeesData]);

  // Lignes affichées, filtrées par équipe le cas échéant
  const visibleEntries = useMemo(
    () =>
      (weekly?.entries ?? []).filter(
        (e) => !effectiveTeam || activityByEmployee.get(e.employee_id) === effectiveTeam,
      ),
    [weekly, effectiveTeam, activityByEmployee],
  );

  const shiftWeek = (days: number) => setWeekStart((d) => addDays(d, days));
  const goToday = () => setWeekStart(startOfISOWeek(new Date()));

  const handleChange = (employee_id: number, schedule_id: number | null) => {
    setOverride.mutate({ employee_id, week_start: weekStartIso, schedule_id });
  };

  /** Ajoute / remplace / retire un jour d'absence en préservant les motifs des autres jours. */
  const setDayAbsence = (
    employee_id: number,
    current: AbsentDay[],
    day: number,
    reason: AbsenceReason | null,
  ) => {
    const others = current.filter((d) => d.day !== day);
    const next = reason ? [...others, { day, reason }] : others;
    setAbsentDays.mutate({ employee_id, week_start: weekStartIso, absent_days: next });
  };

  /** Chauffeurs : affecte/retire une tournée sur un jour (exclusif d'une absence). */
  const setDayRound = (
    employee_id: number,
    absentDetails: AbsentDay[],
    day: number,
    round_id: number | null,
  ) => {
    setDriverDay.mutate({ employee_id, week_start: weekStartIso, day_of_week: day, round_id });
    // Poser une tournée efface une éventuelle absence du même jour
    if (round_id !== null && absentDetails.some((a) => a.day === day)) {
      setDayAbsence(employee_id, absentDetails, day, null);
    }
  };

  /** Chauffeurs : marque une absence sur un jour (retire la tournée du jour). */
  const setDriverAbsence = (
    employee_id: number,
    absentDetails: AbsentDay[],
    dayRounds: DayRound[],
    day: number,
    reason: AbsenceReason | null,
  ) => {
    setDayAbsence(employee_id, absentDetails, day, reason);
    if (reason !== null && dayRounds.some((r) => r.day === day)) {
      setDriverDay.mutate({ employee_id, week_start: weekStartIso, day_of_week: day, round_id: null });
    }
  };

  const setWholeWeekOff = (employee_id: number, current: AbsentDay[], alreadyOff: boolean) => {
    if (alreadyOff) {
      setAbsentDays.mutate({ employee_id, week_start: weekStartIso, absent_days: [] });
    } else {
      // Utilise les motifs déjà présents et remplit les jours manquants en "conge"
      const byDay = new Map(current.map((d) => [d.day, d.reason]));
      const next: AbsentDay[] = [0, 1, 2, 3, 4, 5, 6].map((d) => ({
        day: d,
        reason: (byDay.get(d) ?? 'conge') as AbsenceReason,
      }));
      setAbsentDays.mutate({ employee_id, week_start: weekStartIso, absent_days: next });
    }
  };

  return (
    <div>
      <Header title="Affectation des plannings" />

      <Card
        title={`Semaine ${getISOWeek(weekStart)} · du ${format(weekStart, 'd MMM', { locale: fr })} au ${format(weekEnd, 'd MMM yyyy', { locale: fr })}`}
        action={
          <div className="flex items-center gap-2">
            {!isManager && (
              <select
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value as EmployeeActivity | '')}
                className="rounded-md border border-gray-300 px-2 py-1 text-xs
                           focus:outline-none focus:ring-1 focus:ring-accent"
              >
                <option value="">Toutes les équipes</option>
                {(Object.keys(ACTIVITY_LABEL) as EmployeeActivity[]).map((a) => (
                  <option key={a} value={a}>
                    {ACTIVITY_LABEL[a]}
                  </option>
                ))}
              </select>
            )}
            <select
              value={siteFilter ?? ''}
              onChange={(e) => setSiteFilter(e.target.value ? Number(e.target.value) : undefined)}
              className="rounded-md border border-gray-300 px-2 py-1 text-xs
                         focus:outline-none focus:ring-1 focus:ring-accent"
            >
              <option value="">Tous les sites</option>
              {sites?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <Button
              variant="dark"
              size="sm"
              disabled={!effectiveTeam}
              title={
                effectiveTeam
                  ? `Télécharger le planning ${ACTIVITY_LABEL[effectiveTeam]} de la semaine`
                  : 'Sélectionnez une équipe pour exporter son planning'
              }
              onClick={() =>
                effectiveTeam &&
                downloadWeeklyPlanning({
                  week_start: weekStartIso,
                  activity: effectiveTeam,
                  site_id: siteFilter,
                })
              }
            >
              <FileText size={13} className="mr-1" /> Planning PDF
            </Button>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => shiftWeek(-7)} title="Semaine précédente">
                <ChevronLeft size={14} />
              </Button>
              <Button variant="secondary" size="sm" onClick={goToday}>
                Aujourd'hui
              </Button>
              <Button variant="ghost" size="sm" onClick={() => shiftWeek(7)} title="Semaine suivante">
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        }
      >
        {isLoading ? (
          <p className="text-sm text-gray-400">Chargement...</p>
        ) : visibleEntries.length === 0 ? (
          <p className="text-sm text-gray-400">Aucun employé.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4 font-medium">Employé</th>
                  <th className="py-2 pr-4 font-medium">Métier</th>
                  <th className="py-2 pr-4 font-medium">Site</th>
                  <th className="py-2 pr-4 font-medium">Planning de la semaine</th>
                  <th className="py-2 pr-4 font-medium">Jours</th>
                  <th className="py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleEntries.map((entry) => {
                  const color = colorFor(entry.schedule_id);
                  const allOff = entry.absent_days.length === 7;
                  const activity = activityByEmployee.get(entry.employee_id);
                  const isDriver = activity === 'driving';
                  // Ne proposer que les plannings du métier de l'employé
                  const options = (schedules ?? []).filter((s) => s.activity === activity);
                  return (
                    <tr key={entry.employee_id} className="border-b border-gray-100 last:border-0">
                      <td className="py-2 pr-4 font-medium text-gray-900">{entry.employee_name}</td>
                      <td className="py-2 pr-4">
                        {activity ? (
                          <Badge variant="neutral">{ACTIVITY_LABEL[activity]}</Badge>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-gray-600">{entry.site_name ?? '—'}</td>
                      <td className="py-2 pr-4">
                        {isDriver ? (
                          // Chauffeurs : la tournée se choisit jour par jour (colonne Jours)
                          <span className="text-[11px] text-gray-400 italic">Tournée par jour →</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            {color && <span className={`w-2 h-2 rounded-full ${color.bar}`} />}
                            <select
                              value={entry.schedule_id ?? ''}
                              onChange={(e) =>
                                handleChange(
                                  entry.employee_id,
                                  e.target.value ? Number(e.target.value) : null,
                                )
                              }
                              className={`rounded-md border px-2 py-1 text-xs
                                         focus:outline-none focus:ring-1 focus:ring-accent
                                         ${color ? `${color.bg} ${color.text} ${color.border}` : 'border-gray-300 text-gray-500'}`}
                            >
                              <option value="">— Aucun —</option>
                              {options.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                            {options.length === 0 && (
                              <span className="text-[11px] text-gray-400 italic">
                                Aucun planning pour ce métier
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {isDriver ? (
                          <DriverDayCells
                            dayRounds={entry.day_rounds}
                            workingDays={entry.working_days}
                            absentDetails={entry.absent_details}
                            roundsList={roundsList ?? []}
                            onSetRound={(day, round_id) =>
                              setDayRound(entry.employee_id, entry.absent_details, day, round_id)
                            }
                            onSetAbsence={(day, reason) =>
                              setDriverAbsence(
                                entry.employee_id,
                                entry.absent_details,
                                entry.day_rounds,
                                day,
                                reason,
                              )
                            }
                          />
                        ) : (
                          <DayCells
                            workingDays={entry.working_days}
                            absentDetails={entry.absent_details}
                            barClass={color?.bar ?? 'bg-gray-400'}
                            onSet={(day, reason) =>
                              setDayAbsence(entry.employee_id, entry.absent_details, day, reason)
                            }
                          />
                        )}
                      </td>
                      <td className="py-2 text-right">
                        <div className="inline-flex items-center gap-3">
                          <button
                            onClick={() => setWholeWeekOff(entry.employee_id, entry.absent_details, allOff)}
                            className={`inline-flex items-center gap-1 text-xs ${
                              allOff ? 'text-rose-600 hover:text-rose-800' : 'text-gray-500 hover:text-gray-800'
                            }`}
                            title={allOff ? "Retirer l'absence de toute la semaine" : 'Marquer la semaine comme absente (congé)'}
                          >
                            <CalendarOff size={12} /> {allOff ? 'Présent' : 'Semaine off'}
                          </button>
                          {!isDriver && entry.is_override && (
                            <button
                              onClick={() => handleChange(entry.employee_id, null)}
                              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
                              title="Retirer la sélection de cette semaine"
                            >
                              <RotateCcw size={12} /> Annuler
                            </button>
                          )}
                          {activity && (
                            <button
                              onClick={() =>
                                downloadWeeklyPlanning({
                                  week_start: weekStartIso,
                                  activity,
                                  employee_id: entry.employee_id,
                                })
                              }
                              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
                              title="Télécharger le planning PDF de cet employé"
                            >
                              <FileText size={12} /> PDF
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// --- Grille L-M-M-J-V-S-D cliquable pour marquer les absences ---

// Libellés FR + style par motif d'absence
const REASON_LABEL: Record<AbsenceReason, string> = {
  repos: 'Repos',
  astreinte: 'Astreinte',
  conge: 'Congé',
  maladie: 'Maladie',
  autre: 'Autre',
};
const REASON_STYLE: Record<AbsenceReason, string> = {
  repos: 'bg-slate-200 text-slate-600 border-slate-300',
  astreinte: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  conge: 'bg-sky-100 text-sky-700 border-sky-300',
  maladie: 'bg-rose-100 text-rose-700 border-rose-300',
  autre: 'bg-amber-100 text-amber-700 border-amber-300',
};
// "repos" (jour de repos, badge non requis) et "astreinte" (disponible si
// besoin) sont affichés en neutre sans rature ; les autres motifs sont de
// vraies absences, barrées.
const REPOS: AbsenceReason = 'repos';
const NON_BARRES: AbsenceReason[] = ['repos', 'astreinte'];

function DayCells({
  workingDays,
  absentDetails,
  barClass,
  onSet,
}: {
  workingDays: number[];
  absentDetails: AbsentDay[];
  barClass: string;
  onSet: (day: number, reason: AbsenceReason | null) => void;
}) {
  const workSet = new Set(workingDays);
  const absByDay = new Map(absentDetails.map((a) => [a.day, a.reason]));
  const [openState, setOpenState] = useState<{ day: number; x: number; y: number } | null>(null);
  const btnRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  const openMenu = (d: number) => {
    if (openState?.day === d) {
      setOpenState(null);
      return;
    }
    const btn = btnRefs.current[d];
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setOpenState({ day: d, x: rect.left, y: rect.bottom + 4 });
  };

  const currentReason = openState ? absByDay.get(openState.day) : undefined;
  const isOpenAbsent = !!currentReason;

  return (
    <div className="flex items-center gap-1">
      {[0, 1, 2, 3, 4, 5, 6].map((d) => {
        const reason = absByDay.get(d);
        const marked = !!reason;
        const nonBarre = !!reason && NON_BARRES.includes(reason);
        const works = workSet.has(d);
        const base =
          'w-6 h-6 rounded text-[10px] font-semibold flex items-center justify-center border transition-colors cursor-pointer';
        const style = marked
          ? nonBarre
            ? REASON_STYLE[reason] // repos / astreinte : neutre, sans rature
            : `${REASON_STYLE[reason]} line-through`
          : works
          ? `${barClass} text-white border-transparent hover:opacity-80`
          : 'bg-gray-100 text-gray-400 border-gray-200 hover:bg-gray-200';
        const title = marked
          ? reason === REPOS
            ? 'Repos — badge non requis (clic pour changer)'
            : reason === 'astreinte'
            ? 'Astreinte — disponible si besoin (clic pour changer)'
            : `Absent — ${REASON_LABEL[reason]} (clic pour changer)`
          : 'Cliquer pour marquer repos / astreinte / absent';
        return (
          <button
            key={d}
            ref={(el) => (btnRefs.current[d] = el)}
            onClick={() => openMenu(d)}
            className={`${base} ${style}`}
            title={title}
          >
            {JOURS[d][0]}
          </button>
        );
      })}
      {openState &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setOpenState(null)}
            />
            <div
              className="fixed z-50 w-40 bg-white rounded-lg border border-stone-200 shadow-lg py-1 text-xs"
              style={{ left: openState.x, top: openState.y }}
            >
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-stone-500 font-semibold border-b border-stone-100">
                Repos / absence
              </div>
              {(['repos', 'astreinte', 'conge', 'maladie', 'autre'] as AbsenceReason[]).map((r) => {
                const active = currentReason === r;
                return (
                  <button
                    key={r}
                    onClick={() => {
                      onSet(openState.day, r);
                      setOpenState(null);
                    }}
                    className={`w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-stone-50 ${
                      active ? 'font-semibold' : ''
                    }`}
                  >
                    <span className={`w-3 h-3 rounded border ${REASON_STYLE[r]}`} />
                    {REASON_LABEL[r]}
                    {active && <span className="ml-auto text-accent">✓</span>}
                  </button>
                );
              })}
              {isOpenAbsent && (
                <>
                  <div className="border-t border-stone-100 my-1" />
                  <button
                    onClick={() => {
                      onSet(openState.day, null);
                      setOpenState(null);
                    }}
                    className="w-full text-left px-3 py-1.5 text-stone-500 hover:bg-stone-50"
                  >
                    {currentReason && NON_BARRES.includes(currentReason)
                      ? `Retirer ${currentReason === REPOS ? 'le repos' : "l'astreinte"}`
                      : "Retirer l'absence"}
                  </button>
                </>
              )}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}

// --- Grille L-M-M-J-V-S-D pour chauffeurs : une tournée (ou absence) par jour ---

const REASONS_ORDER: AbsenceReason[] = ['repos', 'astreinte', 'conge', 'maladie', 'autre'];

function DriverDayCells({
  dayRounds,
  workingDays,
  absentDetails,
  roundsList,
  onSetRound,
  onSetAbsence,
}: {
  dayRounds: DayRound[];
  workingDays: number[];
  absentDetails: AbsentDay[];
  roundsList: Round[];
  onSetRound: (day: number, round_id: number | null) => void;
  onSetAbsence: (day: number, reason: AbsenceReason | null) => void;
}) {
  const roundByDay = new Map(dayRounds.map((r) => [r.day, r]));
  // Jours travaillés sans tournée (planning hérité) : à distinguer d'un jour libre
  const workingSet = new Set(workingDays);
  const absByDay = new Map(absentDetails.map((a) => [a.day, a.reason]));
  const [openState, setOpenState] = useState<{ day: number; x: number; y: number } | null>(null);
  const btnRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  const openMenu = (d: number) => {
    if (openState?.day === d) {
      setOpenState(null);
      return;
    }
    const btn = btnRefs.current[d];
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setOpenState({ day: d, x: rect.left, y: rect.bottom + 4 });
  };

  const currentRound = openState ? roundByDay.get(openState.day) : undefined;
  const currentReason = openState ? absByDay.get(openState.day) : undefined;

  return (
    <div className="flex items-center gap-1">
      {[0, 1, 2, 3, 4, 5, 6].map((d) => {
        // L'absence prime sur la tournée pour l'affichage de la cellule
        const reason = absByDay.get(d);
        const round = roundByDay.get(d);
        const color = round ? colorFor(round.round_id) : null;
        let style: string;
        let label: string;
        let title: string;
        if (reason) {
          const nonBarre = NON_BARRES.includes(reason);
          style = `${REASON_STYLE[reason]}${nonBarre ? '' : ' line-through'}`;
          label = REASON_LABEL[reason];
          title = `${REASON_LABEL[reason]} (clic pour changer)`;
        } else if (round) {
          style = color
            ? `${color.bg} ${color.text} ${color.border}`
            : 'bg-gray-100 text-gray-500 border-gray-200';
          label = round.round_name;
          title = `${round.round_name} (clic pour changer)`;
        } else if (workingSet.has(d)) {
          style = 'bg-blue-50 text-blue-700 border-blue-200';
          label = 'Travaillé';
          title = 'Jour travaillé sans tournée (clic pour en choisir une)';
        } else {
          style = 'bg-gray-100 text-gray-400 border-gray-200';
          label = '—';
          title = 'Cliquer pour choisir une tournée ou marquer une absence';
        }
        return (
          <button
            key={d}
            ref={(el) => (btnRefs.current[d] = el)}
            onClick={() => openMenu(d)}
            className={`w-16 rounded border px-1 py-1 flex flex-col items-center gap-0.5 transition-colors hover:opacity-80 ${style}`}
            title={title}
          >
            <span className="text-[9px] uppercase font-semibold opacity-60">{JOURS[d][0]}</span>
            <span className="w-full truncate text-center text-[10px] font-semibold leading-tight">
              {label}
            </span>
          </button>
        );
      })}
      {openState &&
        createPortal(
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpenState(null)} />
            <div
              className="fixed z-50 w-48 bg-white rounded-lg border border-stone-200 shadow-lg py-1 text-xs"
              style={{ left: openState.x, top: openState.y }}
            >
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-stone-500 font-semibold border-b border-stone-100">
                Tournée
              </div>
              <div className="max-h-40 overflow-y-auto">
                {roundsList.length === 0 && (
                  <p className="px-3 py-1.5 text-stone-400 italic">Aucune tournée configurée</p>
                )}
                {roundsList.map((r) => {
                  const active = currentRound?.round_id === r.id;
                  const rc = colorFor(r.id);
                  return (
                    <button
                      key={r.id}
                      onClick={() => {
                        onSetRound(openState.day, r.id);
                        setOpenState(null);
                      }}
                      className={`w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-stone-50 ${
                        active ? 'font-semibold' : ''
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${rc?.bar ?? 'bg-gray-400'}`} />
                      <span className="truncate">{r.name}</span>
                      <span className="ml-auto text-[10px] text-stone-400 tabular-nums shrink-0">
                        {r.start_time && r.end_time
                          ? `${r.start_time}–${r.end_time}`
                          : 'sans horaires'}
                      </span>
                      {active && <span className="text-accent">✓</span>}
                    </button>
                  );
                })}
              </div>
              {currentRound && (
                <button
                  onClick={() => {
                    onSetRound(openState.day, null);
                    setOpenState(null);
                  }}
                  className="w-full text-left px-3 py-1.5 text-stone-500 hover:bg-stone-50 border-t border-stone-100"
                >
                  Retirer la tournée
                </button>
              )}
              <div className="px-3 py-1.5 mt-1 text-[10px] uppercase tracking-wide text-stone-500 font-semibold border-y border-stone-100">
                Repos / absence
              </div>
              {REASONS_ORDER.map((r) => {
                const active = currentReason === r;
                return (
                  <button
                    key={r}
                    onClick={() => {
                      onSetAbsence(openState.day, r);
                      setOpenState(null);
                    }}
                    className={`w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-stone-50 ${
                      active ? 'font-semibold' : ''
                    }`}
                  >
                    <span className={`w-3 h-3 rounded border ${REASON_STYLE[r]}`} />
                    {REASON_LABEL[r]}
                    {active && <span className="ml-auto text-accent">✓</span>}
                  </button>
                );
              })}
              {currentReason && (
                <button
                  onClick={() => {
                    onSetAbsence(openState.day, null);
                    setOpenState(null);
                  }}
                  className="w-full text-left px-3 py-1.5 text-stone-500 hover:bg-stone-50 border-t border-stone-100"
                >
                  {NON_BARRES.includes(currentReason)
                    ? `Retirer ${currentReason === REPOS ? 'le repos' : "l'astreinte"}`
                    : "Retirer l'absence"}
                </button>
              )}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
